import { Typography } from 'Components/atoms/Typography';
import { CartesianDataset, CartesianSeries } from 'Core-utils/types/types';
import { useRef, useState, useEffect, useMemo } from 'react';
import { Chart } from 'chart.js';
import { EmptyState } from '../EmptyState';
import EngageIcon from 'Assets/icons/engagement.svg';
import { getOptionsForTheme, AMP_CHART_SCALES_SECONDARY_Y_CONFIG } from 'Core-utils/constants/charts';

export interface CartesianChartProps {
  dataset?: CartesianDataset;
  labels?: string[];
  options?: Record<string, any>;
  title?: string;
  subtitle?: string;
};

export type PrepareChartResult = {
  result: any;
  success: boolean;
  error: Error|null;
};

const usePrepareChartData = (
  chartRef: React.RefObject<HTMLCanvasElement>,
  data: CartesianSeries[],
  labels?: string[],
  options?: Record<string, any>) => {
  const [prepareChartResult, setPrepareChartResult] = useState<PrepareChartResult>({ result: null, success: false, error: null });
  useEffect(() => {
    if (!data) {
      setPrepareChartResult({ result: null, success: false, error: new Error('Unable to render chart: Data is required') });
      return;
    }

    if (!labels) {
      setPrepareChartResult({ result: null, success: false, error: new Error('Unable to render chart: Labels are required') });
      return;
    }

    if (!chartRef.current) {
      setPrepareChartResult({ result: null, success: false, error: new Error('Unable to render chart: Chart reference is required') });
      return;
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) {
      setPrepareChartResult({ result: null, success: false, error: new Error('Unable to render chart: An internal error occurred') });
      return;
    }

    // Update the styles of any yAxisID that is not 'y' to the secondary yAxis style
    const yAxisIDs = Array.from(new Set(data.map((s) => s.yAxisID).filter(Boolean) as string[]));
    const yAxisLabels = data.map((s) => s.label);
    yAxisIDs.forEach((id, index) => {
      if (id !== 'y') {
        options!.scales![id] = AMP_CHART_SCALES_SECONDARY_Y_CONFIG;
      }
      options!.scales![id].title.text = yAxisLabels[index];
    });

    const chartInstance = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels ?? [],
        datasets: data,
      },
      options: options,
    });
    chartInstance.update();
    setPrepareChartResult({ result: chartInstance, success: true, error: null });
    return () => {
      if (chartInstance) {
        chartInstance.destroy();
      }
    };
  }, [chartRef, data, options]);
  return prepareChartResult;
};

const CartesianChart = (props: CartesianChartProps) => {
  if (!props.dataset) {
    return <EmptyState ImageSrc={EngageIcon} description="No data available" />;
  }

  const chartJsRef = useRef<HTMLCanvasElement>(null);
  const data = props.dataset.data;
  const options = useMemo(
    () => getOptionsForTheme(props.title, props.subtitle),
    [props.title, props.subtitle]
  );
  const prepareChartResult = usePrepareChartData(chartJsRef, data, props.labels, options);
  return (
    <div className="chart-wrapper d-flex flex-column gap-2 justify-content-center align-items-center">
      <canvas ref={chartJsRef} />
      {!prepareChartResult.success && <Typography variant="subtitle1" color="text-high-emphasis">{prepareChartResult.error?.message}</Typography>}
    </div>
  );
};

export default CartesianChart;